import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Trash2, CheckCircle2, XCircle, Clock, Users, Mail, Phone, MessageSquare, Calendar as CalIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = (s) => {
  const map = {
    pending: { label: "En attente", cls: "bg-amber-100 text-amber-800" },
    confirmed: { label: "Confirmée", cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "Annulée", cls: "bg-gray-200 text-gray-700" },
  };
  const m = map[s] || map.pending;
  return <span className={`text-xs px-2 py-1 rounded-md font-semibold ${m.cls}`}>{m.label}</span>;
};

const AdminReservations = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reservations`);
      setItems(data);
      setError(null);
    } catch (e) {
      setError("Impossible de charger les réservations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/reservations/${id}?status=${status}`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réservation ?")) return;
    await axios.delete(`${API}/reservations/${id}`);
    load();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-[#1B7B71] text-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6">
          <h1 className="text-2xl font-bold tracking-tight">BAAN MAE · Réservations</h1>
          <p className="text-sm text-[#cde8e4]">Vue administrateur</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">{items.length} réservation{items.length>1?"s":""}</p>
          <button onClick={load} className="text-sm font-semibold text-[#1B7B71] hover:underline">Actualiser</button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
        )}
        {error && <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">{error}</div>}

        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{r.name}</span>
                    {statusBadge(r.status)}
                    {r.emails_sent ? (
                      <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">e-mails envoyés</span>
                    ) : (
                      <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">e-mails MOCK</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Créée le {new Date(r.created_at).toLocaleString("fr-FR")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setStatus(r.id, "confirmed")} className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Confirmer
                  </button>
                  <button onClick={() => setStatus(r.id, "cancelled")} className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                    <XCircle className="h-3.5 w-3.5" /> Annuler
                  </button>
                  <button onClick={() => remove(r.id)} className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-sm">
                <div className="flex items-center gap-2 text-gray-700"><CalIcon className="h-4 w-4 text-[#1B7B71]" />{r.date}</div>
                <div className="flex items-center gap-2 text-gray-700"><Clock className="h-4 w-4 text-[#1B7B71]" />{r.time}</div>
                <div className="flex items-center gap-2 text-gray-700"><Users className="h-4 w-4 text-[#1B7B71]" />{r.guests} pers.</div>
                <div className="flex items-center gap-2 text-gray-700 truncate"><Mail className="h-4 w-4 text-[#1B7B71]" /><a href={`mailto:${r.email}`} className="hover:underline truncate">{r.email}</a></div>
                {r.phone && (
                  <div className="flex items-center gap-2 text-gray-700"><Phone className="h-4 w-4 text-[#1B7B71]" /><a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a></div>
                )}
              </div>
              {r.message && (
                <div className="flex items-start gap-2 mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <MessageSquare className="h-4 w-4 text-[#1B7B71] mt-0.5 flex-shrink-0" />
                  <span>{r.message}</span>
                </div>
              )}
            </div>
          ))}

          {!loading && items.length === 0 && (
            <div className="text-center py-16 text-gray-500">Aucune réservation pour le moment.</div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminReservations;
